/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const Be = function(t) {
  const e = [];
  let n = 0;
  for (let r = 0; r < t.length; r++) {
    let s = t.charCodeAt(r);
    s < 128 ? e[n++] = s : s < 2048 ? (e[n++] = s >> 6 | 192, e[n++] = s & 63 | 128) : (s & 64512) === 55296 && r + 1 < t.length && (t.charCodeAt(r + 1) & 64512) === 56320 ? (s = 65536 + ((s & 1023) << 10) + (t.charCodeAt(++r) & 1023), e[n++] = s >> 18 | 240, e[n++] = s >> 12 & 63 | 128, e[n++] = s >> 6 & 63 | 128, e[n++] = s & 63 | 128) : (e[n++] = s >> 12 | 224, e[n++] = s >> 6 & 63 | 128, e[n++] = s & 63 | 128);
  }
  return e;
}, ct = function(t) {
  const e = [];
  let n = 0, r = 0;
  for (; n < t.length; ) {
    const s = t[n++];
    if (s < 128)
      e[r++] = String.fromCharCode(s);
    else if (s > 191 && s < 224) {
      const i = t[n++];
      e[r++] = String.fromCharCode((s & 31) << 6 | i & 63);
    } else if (s > 239 && s < 365) {
      const i = t[n++], a = t[n++], c = t[n++], o = ((s & 7) << 18 | (i & 63) << 12 | (a & 63) << 6 | c & 63) - 65536;
      e[r++] = String.fromCharCode(55296 + (o >> 10)), e[r++] = String.fromCharCode(56320 + (o & 1023));
    } else {
      const i = t[n++], a = t[n++];
      e[r++] = String.fromCharCode((s & 15) << 12 | (i & 63) << 6 | a & 63);
    }
  }
  return e.join("");
}, Re = {
  /**
   * Maps bytes to characters.
   */
  byteToCharMap_: null,
  /**
   * Maps characters to bytes.
   */
  charToByteMap_: null,
  /**
   * Maps bytes to websafe characters.
   * @private
   */
  byteToCharMapWebSafe_: null,
  /**
   * Maps websafe characters to bytes.
   * @private
   */
  charToByteMapWebSafe_: null,
  /**
   * Our default alphabet, shared between
   * ENCODED_VALS and ENCODED_VALS_WEBSAFE
   */
  ENCODED_VALS_BASE: "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789",
  /**
   * Our default alphabet. Value 64 (=) is special; it means "nothing."
   */
  get ENCODED_VALS() {
    return this.ENCODED_VALS_BASE + "+/=";
  },
  /**
   * Our websafe alphabet.
   */
  get ENCODED_VALS_WEBSAFE() {
    return this.ENCODED_VALS_BASE + "-_.";
  },
  /**
   * Whether this browser supports the atob and btoa functions. This extension
   * started at Mozilla but is now implemented by many browsers. We use the
   * ASSUME_* variables to avoid pulling in the full useragent detection library
   * but still allowing the standard per-browser compilations.
   *
   */
  HAS_NATIVE_SUPPORT: typeof atob == "function",
  /**
   * Base64-encode an array of bytes.
   *
   * @param input An array of bytes (numbers with
   *     value in [0, 255]) to encode.
   * @param webSafe Boolean indicating we should use the
   *     alternative alphabet.
   * @return The base64 encoded string.
   */
  encodeByteArray(t, e) {
    if (!Array.isArray(t))
      throw Error("encodeByteArray takes an array as a parameter");
    this.init_();
    const n = e ? this.byteToCharMapWebSafe_ : this.byteToCharMap_, r = [];
    for (let s = 0; s < t.length; s += 3) {
      const i = t[s], a = s + 1 < t.length, c = a ? t[s + 1] : 0, o = s + 2 < t.length, l = o ? t[s + 2] : 0, h = i >> 2, d = (i & 3) << 4 | c >> 4;
      let g = (c & 15) << 2 | l >> 6, m = l & 63;
      o || (m = 64, a || (g = 64)), r.push(n[h], n[d], n[g], n[m]);
    }
    return r.join("");
  },
  /**
   * Base64-encode a string.
   *
   * @param input A string to encode.
   * @param webSafe If true, we should use the
   *     alternative alphabet.
   * @return The base64 encoded string.
   */
  encodeString(t, e) {
    return this.HAS_NATIVE_SUPPORT && !e ? btoa(t) : this.encodeByteArray(Be(t), e);
  },
  /**
   * Base64-decode a string.
   *
   * @param input to decode.
   * @param webSafe True if we should use the
   *     alternative alphabet.
   * @return string representing the decoded value.
   */
  decodeString(t, e) {
    return this.HAS_NATIVE_SUPPORT && !e ? atob(t) : ct(this.decodeStringToByteArray(t, e));
  },
  /**
   * Base64-decode a string.
   *
   * In base-64 decoding, groups of four characters are converted into three
   * bytes.  If the encoder did not apply padding, the input length may not
   * be a multiple of 4.
   *
   * In this case, the last group will have fewer than 4 characters, and
   * padding will be inferred.  If the group has one or two characters, it decodes
   * to one byte.  If the group has three characters, it decodes to two bytes.
   *
   * @param input Input to decode.
   * @param webSafe True if we should use the web-safe alphabet.
   * @return bytes representing the decoded value.
   */
  decodeStringToByteArray(t, e) {
    this.init_();
    const n = e ? this.charToByteMapWebSafe_ : this.charToByteMap_, r = [];
    for (let s = 0; s < t.length; ) {
      const i = n[t.charAt(s++)], c = s < t.length ? n[t.charAt(s)] : 0;
      ++s;
      const l = s < t.length ? n[t.charAt(s)] : 64;
      ++s;
      const d = s < t.length ? n[t.charAt(s)] : 64;
      if (++s, i == null || c == null || l == null || d == null)
        throw new lt();
      const g = i << 2 | c >> 4;
      if (r.push(g), l !== 64) {
        const m = c << 4 & 240 | l >> 2;
        if (r.push(m), d !== 64) {
          const b = l << 6 & 192 | d;
          r.push(b);
        }
      }
    }
    return r;
  },
  /**
   * Lazy static initialization function. Called before
   * accessing any of the static map variables.
   * @private
   */
  init_() {
    if (!this.byteToCharMap_) {
      this.byteToCharMap_ = {}, this.charToByteMap_ = {}, this.byteToCharMapWebSafe_ = {}, this.charToByteMapWebSafe_ = {};
      for (let t = 0; t < this.ENCODED_VALS.length; t++)
        this.byteToCharMap_[t] = this.ENCODED_VALS.charAt(t), this.charToByteMap_[this.byteToCharMap_[t]] = t, this.byteToCharMapWebSafe_[t] = this.ENCODED_VALS_WEBSAFE.charAt(t), this.charToByteMapWebSafe_[this.byteToCharMapWebSafe_[t]] = t, t >= this.ENCODED_VALS_BASE.length && (this.charToByteMap_[this.ENCODED_VALS_WEBSAFE.charAt(t)] = t, this.charToByteMapWebSafe_[this.ENCODED_VALS.charAt(t)] = t);
    }
  }
};
class lt extends Error {
  constructor() {
    super(...arguments), this.name = "DecodeBase64StringError";
  }
}
const ut = function(t) {
  const e = Be(t);
  return Re.encodeByteArray(e, !0);
}, Fe = function(t) {
  return ut(t).replace(/\./g, "");
}, ht = function(t) {
  try {
    return Re.decodeString(t, !0);
  } catch (e) {
    console.error("base64Decode failed: ", e);
  }
  return null;
};
/**
 * @license
 * Copyright 2022 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
function ft() {
  if (typeof self < "u")
    return self;
  if (typeof window < "u")
    return window;
  if (typeof global < "u")
    return global;
  throw new Error("Unable to locate global object.");
}
/**
 * @license
 * Copyright 2022 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const dt = () => ft().__FIREBASE_DEFAULTS__, gt = () => {
  if (typeof process > "u" || typeof process.env > "u")
    return;
  const t = process.env.__FIREBASE_DEFAULTS__;
  if (t)
    return JSON.parse(t);
}, pt = () => {
  if (typeof document > "u")
    return;
  let t;
  try {
    t = document.cookie.match(/__FIREBASE_DEFAULTS__=([^;]+)/);
  } catch {
    return;
  }
  const e = t && ht(t[1]);
  return e && JSON.parse(e);
}, mt = () => {
  try {
    return dt() || gt() || pt();
  } catch (t) {
    console.info(`Unable to get __FIREBASE_DEFAULTS__ due to: ${t}`);
    return;
  }
}, Pe = () => {
  var t;
  return (t = mt()) === null || t === void 0 ? void 0 : t.config;
};
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class bt {
  constructor() {
    this.reject = () => {
    }, this.resolve = () => {
    }, this.promise = new Promise((e, n) => {
      this.resolve = e, this.reject = n;
    });
  }
  /**
   * Our API internals are not promiseified and cannot because our callback APIs have subtle expectations around
   * invoking promises inline, which Promises are forbidden to do. This method accepts an optional node-style callback
   * and returns a node-style callback which will resolve or reject the Deferred's promise.
   */
  wrapCallback(e) {
    return (n, r) => {
      n ? this.reject(n) : this.resolve(r), typeof e == "function" && (this.promise.catch(() => {
      }), e.length === 1 ? e(n) : e(n, r));
    };
  }
}
function Ne() {
  try {
    return typeof indexedDB == "object";
  } catch {
    return !1;
  }
}
function wt() {
  return new Promise((t, e) => {
    try {
      let n = !0;
      const r = "validate-browser-context-for-indexeddb-analytics-module", s = self.indexedDB.open(r);
      s.onsuccess = () => {
        s.result.close(), n || self.indexedDB.deleteDatabase(r), t(!0);
      }, s.onupgradeneeded = () => {
        n = !1;
      }, s.onerror = () => {
        var i;
        e(((i = s.error) === null || i === void 0 ? void 0 : i.message) || "");
      };
    } catch (n) {
      e(n);
    }
  });
}
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const _t = "FirebaseError";
class y extends Error {
  constructor(e, n, r) {
    super(n), this.code = e, this.customData = r, this.name = _t, Object.setPrototypeOf(this, y.prototype), Error.captureStackTrace && Error.captureStackTrace(this, k.prototype.create);
  }
}
class k {
  constructor(e, n, r) {
    this.service = e, this.serviceName = n, this.errors = r;
  }
  create(e, ...n) {
    const r = n[0] || {}, s = `${this.service}/${e}`, i = this.errors[e], a = i ? Et(i, r) : "Error", c = `${this.serviceName}: ${a} (${s}).`;
    return new y(s, c, r);
  }
}
function Et(t, e) {
  return t.replace(It, (n, r) => {
    const s = e[r];
    return s != null ? String(s) : `<${r}?>`;
  });
}
const It = /\{\$([^}]+)}/g;
function Z(t, e) {
  if (t === e)
    return !0;
  const n = Object.keys(t), r = Object.keys(e);
  for (const s of n) {
    if (!r.includes(s))
      return !1;
    const i = t[s], a = e[s];
    if (pe(i) && pe(a)) {
      if (!Z(i, a))
        return !1;
    } else if (i !== a)
      return !1;
  }
  for (const s of r)
    if (!n.includes(s))
      return !1;
  return !0;
}
function pe(t) {
  return t !== null && typeof t == "object";
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const yt = 1e3, St = 2, Ct = 4 * 60 * 60 * 1e3, At = 0.5;
function Tt(t, e = yt, n = St) {
  const r = e * Math.pow(n, t), s = Math.round(
    // A fraction of the backoff value to add/subtract.
    // Deviation: changes multiplication order to improve readability.
    At * r * // A random float (rounded to int by Math.round above) in the range [-1, 1]. Determines
    // if we add or subtract.
    (Math.random() - 0.5) * 2
  );
  return Math.min(Ct, r + s);
}
/**
 * @license
 * Copyright 2021 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
function B(t) {
  return t && t._delegate ? t._delegate : t;
}
class v {
  /**
   *
   * @param name The public service name, e.g. app, auth, firestore, database
   * @param instanceFactory Service factory responsible for creating the public interface
   * @param type whether the service provided by the component is public or private
   */
  constructor(e, n, r) {
    this.name = e, this.instanceFactory = n, this.type = r, this.multipleInstances = !1, this.serviceProps = {}, this.instantiationMode = "LAZY", this.onInstanceCreated = null;
  }
  setInstantiationMode(e) {
    return this.instantiationMode = e, this;
  }
  setMultipleInstances(e) {
    return this.multipleInstances = e, this;
  }
  setServiceProps(e) {
    return this.serviceProps = e, this;
  }
  setInstanceCreatedCallback(e) {
    return this.onInstanceCreated = e, this;
  }
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const S = "[DEFAULT]";
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class vt {
  constructor(e, n) {
    this.name = e, this.container = n, this.component = null, this.instances = /* @__PURE__ */ new Map(), this.instancesDeferred = /* @__PURE__ */ new Map(), this.instancesOptions = /* @__PURE__ */ new Map(), this.onInitCallbacks = /* @__PURE__ */ new Map();
  }
  /**
   * @param identifier A provider can provide mulitple instances of a service
   * if this.component.multipleInstances is true.
   */
  get(e) {
    const n = this.normalizeInstanceIdentifier(e);
    if (!this.instancesDeferred.has(n)) {
      const r = new bt();
      if (this.instancesDeferred.set(n, r), this.isInitialized(n) || this.shouldAutoInitialize())
        try {
          const s = this.getOrInitializeService({
            instanceIdentifier: n
          });
          s && r.resolve(s);
        } catch {
        }
    }
    return this.instancesDeferred.get(n).promise;
  }
  getImmediate(e) {
    var n;
    const r = this.normalizeInstanceIdentifier(e == null ? void 0 : e.identifier), s = (n = e == null ? void 0 : e.optional) !== null && n !== void 0 ? n : !1;
    if (this.isInitialized(r) || this.shouldAutoInitialize())
      try {
        return this.getOrInitializeService({
          instanceIdentifier: r
        });
      } catch (i) {
        if (s)
          return null;
        throw i;
      }
    else {
      if (s)
        return null;
      throw Error(`Service ${this.name} is not available`);
    }
  }
  getComponent() {
    return this.component;
  }
  setComponent(e) {
    if (e.name !== this.name)
      throw Error(`Mismatching Component ${e.name} for Provider ${this.name}.`);
    if (this.component)
      throw Error(`Component for ${this.name} has already been provided`);
    if (this.component = e, !!this.shouldAutoInitialize()) {
      if (Mt(e))
        try {
          this.getOrInitializeService({ instanceIdentifier: S });
        } catch {
        }
      for (const [n, r] of this.instancesDeferred.entries()) {
        const s = this.normalizeInstanceIdentifier(n);
        try {
          const i = this.getOrInitializeService({
            instanceIdentifier: s
          });
          r.resolve(i);
        } catch {
        }
      }
    }
  }
  clearInstance(e = S) {
    this.instancesDeferred.delete(e), this.instancesOptions.delete(e), this.instances.delete(e);
  }
  // app.delete() will call this method on every provider to delete the services
  // TODO: should we mark the provider as deleted?
  async delete() {
    const e = Array.from(this.instances.values());
    await Promise.all([
      ...e.filter((n) => "INTERNAL" in n).map((n) => n.INTERNAL.delete()),
      ...e.filter((n) => "_delete" in n).map((n) => n._delete())
    ]);
  }
  isComponentSet() {
    return this.component != null;
  }
  isInitialized(e = S) {
    return this.instances.has(e);
  }
  getOptions(e = S) {
    return this.instancesOptions.get(e) || {};
  }
  initialize(e = {}) {
    const { options: n = {} } = e, r = this.normalizeInstanceIdentifier(e.instanceIdentifier);
    if (this.isInitialized(r))
      throw Error(`${this.name}(${r}) has already been initialized`);
    if (!this.isComponentSet())
      throw Error(`Component ${this.name} has not been registered yet`);
    const s = this.getOrInitializeService({
      instanceIdentifier: r,
      options: n
    });
    for (const [i, a] of this.instancesDeferred.entries()) {
      const c = this.normalizeInstanceIdentifier(i);
      r === c && a.resolve(s);
    }
    return s;
  }
  /**
   *
   * @param callback - a function that will be invoked  after the provider has been initialized by calling provider.initialize().
   * The function is invoked SYNCHRONOUSLY, so it should not execute any longrunning tasks in order to not block the program.
   *
   * @param identifier An optional instance identifier
   * @returns a function to unregister the callback
   */
  onInit(e, n) {
    var r;
    const s = this.normalizeInstanceIdentifier(n), i = (r = this.onInitCallbacks.get(s)) !== null && r !== void 0 ? r : /* @__PURE__ */ new Set();
    i.add(e), this.onInitCallbacks.set(s, i);
    const a = this.instances.get(s);
    return a && e(a, s), () => {
      i.delete(e);
    };
  }
  /**
   * Invoke onInit callbacks synchronously
   * @param instance the service instance`
   */
  invokeOnInitCallbacks(e, n) {
    const r = this.onInitCallbacks.get(n);
    if (r)
      for (const s of r)
        try {
          s(e, n);
        } catch {
        }
  }
  getOrInitializeService({ instanceIdentifier: e, options: n = {} }) {
    let r = this.instances.get(e);
    if (!r && this.component && (r = this.component.instanceFactory(this.container, {
      instanceIdentifier: Dt(e),
      options: n
    }), this.instances.set(e, r), this.instancesOptions.set(e, n), this.invokeOnInitCallbacks(r, e), this.component.onInstanceCreated))
      try {
        this.component.onInstanceCreated(this.container, e, r);
      } catch {
      }
    return r || null;
  }
  normalizeInstanceIdentifier(e = S) {
    return this.component ? this.component.multipleInstances ? e : S : e;
  }
  shouldAutoInitialize() {
    return !!this.component && this.component.instantiationMode !== "EXPLICIT";
  }
}
function Dt(t) {
  return t === S ? void 0 : t;
}
function Mt(t) {
  return t.instantiationMode === "EAGER";
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class Ot {
  constructor(e) {
    this.name = e, this.providers = /* @__PURE__ */ new Map();
  }
  /**
   *
   * @param component Component being added
   * @param overwrite When a component with the same name has already been registered,
   * if overwrite is true: overwrite the existing component with the new component and create a new
   * provider with the new component. It can be useful in tests where you want to use different mocks
   * for different tests.
   * if overwrite is false: throw an exception
   */
  addComponent(e) {
    const n = this.getProvider(e.name);
    if (n.isComponentSet())
      throw new Error(`Component ${e.name} has already been registered with ${this.name}`);
    n.setComponent(e);
  }
  addOrOverwriteComponent(e) {
    this.getProvider(e.name).isComponentSet() && this.providers.delete(e.name), this.addComponent(e);
  }
  /**
   * getProvider provides a type safe interface where it can only be called with a field name
   * present in NameServiceMapping interface.
   *
   * Firebase SDKs providing services should extend NameServiceMapping interface to register
   * themselves.
   */
  getProvider(e) {
    if (this.providers.has(e))
      return this.providers.get(e);
    const n = new vt(e, this);
    return this.providers.set(e, n), n;
  }
  getProviders() {
    return Array.from(this.providers.values());
  }
}
/**
 * @license
 * Copyright 2017 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var u;
(function(t) {
  t[t.DEBUG = 0] = "DEBUG", t[t.VERBOSE = 1] = "VERBOSE", t[t.INFO = 2] = "INFO", t[t.WARN = 3] = "WARN", t[t.ERROR = 4] = "ERROR", t[t.SILENT = 5] = "SILENT";
})(u || (u = {}));
const Bt = {
  debug: u.DEBUG,
  verbose: u.VERBOSE,
  info: u.INFO,
  warn: u.WARN,
  error: u.ERROR,
  silent: u.SILENT
}, Rt = u.INFO, Ft = {
  [u.DEBUG]: "log",
  [u.VERBOSE]: "log",
  [u.INFO]: "info",
  [u.WARN]: "warn",
  [u.ERROR]: "error"
}, Pt = (t, e, ...n) => {
  if (e < t.logLevel)
    return;
  const r = (/* @__PURE__ */ new Date()).toISOString(), s = Ft[e];
  if (s)
    console[s](`[${r}]  ${t.name}:`, ...n);
  else
    throw new Error(`Attempted to log a message with an invalid logType (value: ${e})`);
};
class Le {
  /**
   * Gives you an instance of a Logger to capture messages according to
   * Firebase's logging scheme.
   *
   * @param name The name that the logs will be associated with
   */
  constructor(e) {
    this.name = e, this._logLevel = Rt, this._logHandler = Pt, this._userLogHandler = null;
  }
  get logLevel() {
    return this._logLevel;
  }
  set logLevel(e) {
    if (!(e in u))
      throw new TypeError(`Invalid value "${e}" assigned to \`logLevel\``);
    this._logLevel = e;
  }
  // Workaround for setter/getter having to be the same type.
  setLogLevel(e) {
    this._logLevel = typeof e == "string" ? Bt[e] : e;
  }
  get logHandler() {
    return this._logHandler;
  }
  set logHandler(e) {
    if (typeof e != "function")
      throw new TypeError("Value assigned to `logHandler` must be a function");
    this._logHandler = e;
  }
  get userLogHandler() {
    return this._userLogHandler;
  }
  set userLogHandler(e) {
    this._userLogHandler = e;
  }
  /**
   * The functions below are all based on the `console` interface
   */
  debug(...e) {
    this._userLogHandler && this._userLogHandler(this, u.DEBUG, ...e), this._logHandler(this, u.DEBUG, ...e);
  }
  log(...e) {
    this._userLogHandler && this._userLogHandler(this, u.VERBOSE, ...e), this._logHandler(this, u.VERBOSE, ...e);
  }
  info(...e) {
    this._userLogHandler && this._userLogHandler(this, u.INFO, ...e), this._logHandler(this, u.INFO, ...e);
  }
  warn(...e) {
    this._userLogHandler && this._userLogHandler(this, u.WARN, ...e), this._logHandler(this, u.WARN, ...e);
  }
  error(...e) {
    this._userLogHandler && this._userLogHandler(this, u.ERROR, ...e), this._logHandler(this, u.ERROR, ...e);
  }
}
const Nt = (t, e) => e.some((n) => t instanceof n);
let me, be;
function Lt() {
  return me || (me = [
    IDBDatabase,
    IDBObjectStore,
    IDBIndex,
    IDBCursor,
    IDBTransaction
  ]);
}
function $t() {
  return be || (be = [
    IDBCursor.prototype.advance,
    IDBCursor.prototype.continue,
    IDBCursor.prototype.continuePrimaryKey
  ]);
}
const $e = /* @__PURE__ */ new WeakMap(), Q = /* @__PURE__ */ new WeakMap(), ke = /* @__PURE__ */ new WeakMap(), V = /* @__PURE__ */ new WeakMap(), oe = /* @__PURE__ */ new WeakMap();
function kt(t) {
  const e = new Promise((n, r) => {
    const s = () => {
      t.removeEventListener("success", i), t.removeEventListener("error", a);
    }, i = () => {
      n(_(t.result)), s();
    }, a = () => {
      r(t.error), s();
    };
    t.addEventListener("success", i), t.addEventListener("error", a);
  });
  return e.then((n) => {
    n instanceof IDBCursor && $e.set(n, t);
  }).catch(() => {
  }), oe.set(e, t), e;
}
function xt(t) {
  if (Q.has(t))
    return;
  const e = new Promise((n, r) => {
    const s = () => {
      t.removeEventListener("complete", i), t.removeEventListener("error", a), t.removeEventListener("abort", a);
    }, i = () => {
      n(), s();
    }, a = () => {
      r(t.error || new DOMException("AbortError", "AbortError")), s();
    };
    t.addEventListener("complete", i), t.addEventListener("error", a), t.addEventListener("abort", a);
  });
  Q.set(t, e);
}
let ee = {
  get(t, e, n) {
    if (t instanceof IDBTransaction) {
      if (e === "done")
        return Q.get(t);
      if (e === "objectStoreNames")
        return t.objectStoreNames || ke.get(t);
      if (e === "store")
        return n.objectStoreNames[1] ? void 0 : n.objectStore(n.objectStoreNames[0]);
    }
    return _(t[e]);
  },
  set(t, e, n) {
    return t[e] = n, !0;
  },
  has(t, e) {
    return t instanceof IDBTransaction && (e === "done" || e === "store") ? !0 : e in t;
  }
};
function jt(t) {
  ee = t(ee);
}
function Vt(t) {
  return t === IDBDatabase.prototype.transaction && !("objectStoreNames" in IDBTransaction.prototype) ? function(e, ...n) {
    const r = t.call(H(this), e, ...n);
    return ke.set(r, e.sort ? e.sort() : [e]), _(r);
  } : $t().includes(t) ? function(...e) {
    return t.apply(H(this), e), _($e.get(this));
  } : function(...e) {
    return _(t.apply(H(this), e));
  };
}
function Ht(t) {
  return typeof t == "function" ? Vt(t) : (t instanceof IDBTransaction && xt(t), Nt(t, Lt()) ? new Proxy(t, ee) : t);
}
function _(t) {
  if (t instanceof IDBRequest)
    return kt(t);
  if (V.has(t))
    return V.get(t);
  const e = Ht(t);
  return e !== t && (V.set(t, e), oe.set(e, t)), e;
}
const H = (t) => oe.get(t);
function Ut(t, e, { blocked: n, upgrade: r, blocking: s, terminated: i } = {}) {
  const a = indexedDB.open(t, e), c = _(a);
  return r && a.addEventListener("upgradeneeded", (o) => {
    r(_(a.result), o.oldVersion, o.newVersion, _(a.transaction), o);
  }), n && a.addEventListener("blocked", (o) => n(
    // Casting due to https://github.com/microsoft/TypeScript-DOM-lib-generator/pull/1405
    o.oldVersion,
    o.newVersion,
    o
  )), c.then((o) => {
    i && o.addEventListener("close", () => i()), s && o.addEventListener("versionchange", (l) => s(l.oldVersion, l.newVersion, l));
  }).catch(() => {
  }), c;
}
const zt = ["get", "getKey", "getAll", "getAllKeys", "count"], Wt = ["put", "add", "delete", "clear"], U = /* @__PURE__ */ new Map();
function we(t, e) {
  if (!(t instanceof IDBDatabase && !(e in t) && typeof e == "string"))
    return;
  if (U.get(e))
    return U.get(e);
  const n = e.replace(/FromIndex$/, ""), r = e !== n, s = Wt.includes(n);
  if (
    // Bail if the target doesn't exist on the target. Eg, getAll isn't in Edge.
    !(n in (r ? IDBIndex : IDBObjectStore).prototype) || !(s || zt.includes(n))
  )
    return;
  const i = async function(a, ...c) {
    const o = this.transaction(a, s ? "readwrite" : "readonly");
    let l = o.store;
    return r && (l = l.index(c.shift())), (await Promise.all([
      l[n](...c),
      s && o.done
    ]))[0];
  };
  return U.set(e, i), i;
}
jt((t) => ({
  ...t,
  get: (e, n, r) => we(e, n) || t.get(e, n, r),
  has: (e, n) => !!we(e, n) || t.has(e, n)
}));
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class Kt {
  constructor(e) {
    this.container = e;
  }
  // In initial implementation, this will be called by installations on
  // auth token refresh, and installations will send this string.
  getPlatformInfoString() {
    return this.container.getProviders().map((n) => {
      if (qt(n)) {
        const r = n.getImmediate();
        return `${r.library}/${r.version}`;
      } else
        return null;
    }).filter((n) => n).join(" ");
  }
}
function qt(t) {
  const e = t.getComponent();
  return (e == null ? void 0 : e.type) === "VERSION";
}
const te = "@firebase/app", _e = "0.10.1";
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const D = new Le("@firebase/app"), Gt = "@firebase/app-compat", Jt = "@firebase/analytics-compat", Yt = "@firebase/analytics", Xt = "@firebase/app-check-compat", Zt = "@firebase/app-check", Qt = "@firebase/auth", en = "@firebase/auth-compat", tn = "@firebase/database", nn = "@firebase/database-compat", rn = "@firebase/functions", sn = "@firebase/functions-compat", an = "@firebase/installations", on = "@firebase/installations-compat", cn = "@firebase/messaging", ln = "@firebase/messaging-compat", un = "@firebase/performance", hn = "@firebase/performance-compat", fn = "@firebase/remote-config", dn = "@firebase/remote-config-compat", gn = "@firebase/storage", pn = "@firebase/storage-compat", mn = "@firebase/firestore", bn = "@firebase/firestore-compat", wn = "firebase", _n = "10.11.0";
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const ne = "[DEFAULT]", En = {
  [te]: "fire-core",
  [Gt]: "fire-core-compat",
  [Yt]: "fire-analytics",
  [Jt]: "fire-analytics-compat",
  [Zt]: "fire-app-check",
  [Xt]: "fire-app-check-compat",
  [Qt]: "fire-auth",
  [en]: "fire-auth-compat",
  [tn]: "fire-rtdb",
  [nn]: "fire-rtdb-compat",
  [rn]: "fire-fn",
  [sn]: "fire-fn-compat",
  [an]: "fire-iid",
  [on]: "fire-iid-compat",
  [cn]: "fire-fcm",
  [ln]: "fire-fcm-compat",
  [un]: "fire-perf",
  [hn]: "fire-perf-compat",
  [fn]: "fire-rc",
  [dn]: "fire-rc-compat",
  [gn]: "fire-gcs",
  [pn]: "fire-gcs-compat",
  [mn]: "fire-fst",
  [bn]: "fire-fst-compat",
  "fire-js": "fire-js",
  [wn]: "fire-js-all"
};
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const P = /* @__PURE__ */ new Map(), In = /* @__PURE__ */ new Map(), re = /* @__PURE__ */ new Map();
function Ee(t, e) {
  try {
    t.container.addComponent(e);
  } catch (n) {
    D.debug(`Component ${e.name} failed to register with FirebaseApp ${t.name}`, n);
  }
}
function R(t) {
  const e = t.name;
  if (re.has(e))
    return D.debug(`There were multiple attempts to register component ${e}.`), !1;
  re.set(e, t);
  for (const n of P.values())
    Ee(n, t);
  for (const n of In.values())
    Ee(n, t);
  return !0;
}
function ce(t, e) {
  const n = t.container.getProvider("heartbeat").getImmediate({ optional: !0 });
  return n && n.triggerHeartbeat(), t.container.getProvider(e);
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const yn = {
  "no-app": "No Firebase App '{$appName}' has been created - call initializeApp() first",
  "bad-app-name": "Illegal App name: '{$appName}'",
  "duplicate-app": "Firebase App named '{$appName}' already exists with different options or config",
  "app-deleted": "Firebase App named '{$appName}' already deleted",
  "server-app-deleted": "Firebase Server App has been deleted",
  "no-options": "Need to provide options, when not being deployed to hosting via source.",
  "invalid-app-argument": "firebase.{$appName}() takes either no argument or a Firebase App instance.",
  "invalid-log-argument": "First argument to `onLog` must be null or a function.",
  "idb-open": "Error thrown when opening IndexedDB. Original error: {$originalErrorMessage}.",
  "idb-get": "Error thrown when reading from IndexedDB. Original error: {$originalErrorMessage}.",
  "idb-set": "Error thrown when writing to IndexedDB. Original error: {$originalErrorMessage}.",
  "idb-delete": "Error thrown when deleting from IndexedDB. Original error: {$originalErrorMessage}.",
  "finalization-registry-not-supported": "FirebaseServerApp deleteOnDeref field defined but the JS runtime does not support FinalizationRegistry.",
  "invalid-server-app-environment": "FirebaseServerApp is not for use in browser environments."
}, E = new k("app", "Firebase", yn);
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class Sn {
  constructor(e, n, r) {
    this._isDeleted = !1, this._options = Object.assign({}, e), this._config = Object.assign({}, n), this._name = n.name, this._automaticDataCollectionEnabled = n.automaticDataCollectionEnabled, this._container = r, this.container.addComponent(new v(
      "app",
      () => this,
      "PUBLIC"
      /* ComponentType.PUBLIC */
    ));
  }
  get automaticDataCollectionEnabled() {
    return this.checkDestroyed(), this._automaticDataCollectionEnabled;
  }
  set automaticDataCollectionEnabled(e) {
    this.checkDestroyed(), this._automaticDataCollectionEnabled = e;
  }
  get name() {
    return this.checkDestroyed(), this._name;
  }
  get options() {
    return this.checkDestroyed(), this._options;
  }
  get config() {
    return this.checkDestroyed(), this._config;
  }
  get container() {
    return this._container;
  }
  get isDeleted() {
    return this._isDeleted;
  }
  set isDeleted(e) {
    this._isDeleted = e;
  }
  /**
   * This function will throw an Error if the App has already been deleted -
   * use before performing API actions on the App.
   */
  checkDestroyed() {
    if (this.isDeleted)
      throw E.create("app-deleted", { appName: this._name });
  }
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const Cn = _n;
function An(t, e = {}) {
  let n = t;
  typeof e != "object" && (e = { name: e });
  const r = Object.assign({ name: ne, automaticDataCollectionEnabled: !1 }, e), s = r.name;
  if (typeof s != "string" || !s)
    throw E.create("bad-app-name", {
      appName: String(s)
    });
  if (n || (n = Pe()), !n)
    throw E.create(
      "no-options"
      /* AppError.NO_OPTIONS */
    );
  const i = P.get(s);
  if (i) {
    if (Z(n, i.options) && Z(r, i.config))
      return i;
    throw E.create("duplicate-app", { appName: s });
  }
  const a = new Ot(s);
  for (const o of re.values())
    a.addComponent(o);
  const c = new Sn(n, r, a);
  return P.set(s, c), c;
}
function Tn(t = ne) {
  const e = P.get(t);
  if (!e && t === ne && Pe())
    return An();
  if (!e)
    throw E.create("no-app", { appName: t });
  return e;
}
function ss() {
  return Array.from(P.values());
}
function T(t, e, n) {
  var r;
  let s = (r = En[t]) !== null && r !== void 0 ? r : t;
  n && (s += `-${n}`);
  const i = s.match(/\s|\//), a = e.match(/\s|\//);
  if (i || a) {
    const c = [
      `Unable to register library "${s}" with version "${e}":`
    ];
    i && c.push(`library name "${s}" contains illegal characters (whitespace or "/")`), i && a && c.push("and"), a && c.push(`version name "${e}" contains illegal characters (whitespace or "/")`), D.warn(c.join(" "));
    return;
  }
  R(new v(
    `${s}-version`,
    () => ({ library: s, version: e }),
    "VERSION"
    /* ComponentType.VERSION */
  ));
}
/**
 * @license
 * Copyright 2021 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const vn = "firebase-heartbeat-database", Dn = 1, N = "firebase-heartbeat-store";
let z = null;
function xe() {
  return z || (z = Ut(vn, Dn, {
    upgrade: (t, e) => {
      switch (e) {
        case 0:
          try {
            t.createObjectStore(N);
          } catch (n) {
            console.warn(n);
          }
      }
    }
  }).catch((t) => {
    throw E.create("idb-open", {
      originalErrorMessage: t.message
    });
  })), z;
}
async function Mn(t) {
  try {
    const n = (await xe()).transaction(N), r = await n.objectStore(N).get(je(t));
    return await n.done, r;
  } catch (e) {
    if (e instanceof y)
      D.warn(e.message);
    else {
      const n = E.create("idb-get", {
        originalErrorMessage: e == null ? void 0 : e.message
      });
      D.warn(n.message);
    }
  }
}
async function Ie(t, e) {
  try {
    const r = (await xe()).transaction(N, "readwrite");
    await r.objectStore(N).put(e, je(t)), await r.done;
  } catch (n) {
    if (n instanceof y)
      D.warn(n.message);
    else {
      const r = E.create("idb-set", {
        originalErrorMessage: n == null ? void 0 : n.message
      });
      D.warn(r.message);
    }
  }
}
function je(t) {
  return `${t.name}!${t.options.appId}`;
}
/**
 * @license
 * Copyright 2021 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const On = 1024, Bn = 30 * 24 * 60 * 60 * 1e3;
class Rn {
  constructor(e) {
    this.container = e, this._heartbeatsCache = null;
    const n = this.container.getProvider("app").getImmediate();
    this._storage = new Pn(n), this._heartbeatsCachePromise = this._storage.read().then((r) => (this._heartbeatsCache = r, r));
  }
  /**
   * Called to report a heartbeat. The function will generate
   * a HeartbeatsByUserAgent object, update heartbeatsCache, and persist it
   * to IndexedDB.
   * Note that we only store one heartbeat per day. So if a heartbeat for today is
   * already logged, subsequent calls to this function in the same day will be ignored.
   */
  async triggerHeartbeat() {
    var e, n;
    const s = this.container.getProvider("platform-logger").getImmediate().getPlatformInfoString(), i = ye();
    if (!(((e = this._heartbeatsCache) === null || e === void 0 ? void 0 : e.heartbeats) == null && (this._heartbeatsCache = await this._heartbeatsCachePromise, ((n = this._heartbeatsCache) === null || n === void 0 ? void 0 : n.heartbeats) == null)) && !(this._heartbeatsCache.lastSentHeartbeatDate === i || this._heartbeatsCache.heartbeats.some((a) => a.date === i)))
      return this._heartbeatsCache.heartbeats.push({ date: i, agent: s }), this._heartbeatsCache.heartbeats = this._heartbeatsCache.heartbeats.filter((a) => {
        const c = new Date(a.date).valueOf();
        return Date.now() - c <= Bn;
      }), this._storage.overwrite(this._heartbeatsCache);
  }
  /**
   * Returns a base64 encoded string which can be attached to the heartbeat-specific header directly.
   * It also clears all heartbeats from memory as well as in IndexedDB.
   *
   * NOTE: Consuming product SDKs should not send the header if this method
   * returns an empty string.
   */
  async getHeartbeatsHeader() {
    var e;
    if (this._heartbeatsCache === null && await this._heartbeatsCachePromise, ((e = this._heartbeatsCache) === null || e === void 0 ? void 0 : e.heartbeats) == null || this._heartbeatsCache.heartbeats.length === 0)
      return "";
    const n = ye(), { heartbeatsToSend: r, unsentEntries: s } = Fn(this._heartbeatsCache.heartbeats), i = Fe(JSON.stringify({ version: 2, heartbeats: r }));
    return this._heartbeatsCache.lastSentHeartbeatDate = n, s.length > 0 ? (this._heartbeatsCache.heartbeats = s, await this._storage.overwrite(this._heartbeatsCache)) : (this._heartbeatsCache.heartbeats = [], this._storage.overwrite(this._heartbeatsCache)), i;
  }
}
function ye() {
  return (/* @__PURE__ */ new Date()).toISOString().substring(0, 10);
}
function Fn(t, e = On) {
  const n = [];
  let r = t.slice();
  for (const s of t) {
    const i = n.find((a) => a.agent === s.agent);
    if (i) {
      if (i.dates.push(s.date), Se(n) > e) {
        i.dates.pop();
        break;
      }
    } else if (n.push({
      agent: s.agent,
      dates: [s.date]
    }), Se(n) > e) {
      n.pop();
      break;
    }
    r = r.slice(1);
  }
  return {
    heartbeatsToSend: n,
    unsentEntries: r
  };
}
class Pn {
  constructor(e) {
    this.app = e, this._canUseIndexedDBPromise = this.runIndexedDBEnvironmentCheck();
  }
  async runIndexedDBEnvironmentCheck() {
    return Ne() ? wt().then(() => !0).catch(() => !1) : !1;
  }
  /**
   * Read all heartbeats.
   */
  async read() {
    if (await this._canUseIndexedDBPromise) {
      const n = await Mn(this.app);
      return n != null && n.heartbeats ? n : { heartbeats: [] };
    } else
      return { heartbeats: [] };
  }
  // overwrite the storage with the provided heartbeats
  async overwrite(e) {
    var n;
    if (await this._canUseIndexedDBPromise) {
      const s = await this.read();
      return Ie(this.app, {
        lastSentHeartbeatDate: (n = e.lastSentHeartbeatDate) !== null && n !== void 0 ? n : s.lastSentHeartbeatDate,
        heartbeats: e.heartbeats
      });
    } else
      return;
  }
  // add heartbeats
  async add(e) {
    var n;
    if (await this._canUseIndexedDBPromise) {
      const s = await this.read();
      return Ie(this.app, {
        lastSentHeartbeatDate: (n = e.lastSentHeartbeatDate) !== null && n !== void 0 ? n : s.lastSentHeartbeatDate,
        heartbeats: [
          ...s.heartbeats,
          ...e.heartbeats
        ]
      });
    } else
      return;
  }
}
function Se(t) {
  return Fe(
    // heartbeatsCache wrapper properties
    JSON.stringify({ version: 2, heartbeats: t })
  ).length;
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
function Nn(t) {
  R(new v(
    "platform-logger",
    (e) => new Kt(e),
    "PRIVATE"
    /* ComponentType.PRIVATE */
  )), R(new v(
    "heartbeat",
    (e) => new Rn(e),
    "PRIVATE"
    /* ComponentType.PRIVATE */
  )), T(te, _e, t), T(te, _e, "esm2017"), T("fire-js", "");
}
Nn("");
const Ln = (t, e) => e.some((n) => t instanceof n);
let Ce, Ae;
function $n() {
  return Ce || (Ce = [
    IDBDatabase,
    IDBObjectStore,
    IDBIndex,
    IDBCursor,
    IDBTransaction
  ]);
}
function kn() {
  return Ae || (Ae = [
    IDBCursor.prototype.advance,
    IDBCursor.prototype.continue,
    IDBCursor.prototype.continuePrimaryKey
  ]);
}
const Ve = /* @__PURE__ */ new WeakMap(), se = /* @__PURE__ */ new WeakMap(), He = /* @__PURE__ */ new WeakMap(), W = /* @__PURE__ */ new WeakMap(), le = /* @__PURE__ */ new WeakMap();
function xn(t) {
  const e = new Promise((n, r) => {
    const s = () => {
      t.removeEventListener("success", i), t.removeEventListener("error", a);
    }, i = () => {
      n(I(t.result)), s();
    }, a = () => {
      r(t.error), s();
    };
    t.addEventListener("success", i), t.addEventListener("error", a);
  });
  return e.then((n) => {
    n instanceof IDBCursor && Ve.set(n, t);
  }).catch(() => {
  }), le.set(e, t), e;
}
function jn(t) {
  if (se.has(t))
    return;
  const e = new Promise((n, r) => {
    const s = () => {
      t.removeEventListener("complete", i), t.removeEventListener("error", a), t.removeEventListener("abort", a);
    }, i = () => {
      n(), s();
    }, a = () => {
      r(t.error || new DOMException("AbortError", "AbortError")), s();
    };
    t.addEventListener("complete", i), t.addEventListener("error", a), t.addEventListener("abort", a);
  });
  se.set(t, e);
}
let ie = {
  get(t, e, n) {
    if (t instanceof IDBTransaction) {
      if (e === "done")
        return se.get(t);
      if (e === "objectStoreNames")
        return t.objectStoreNames || He.get(t);
      if (e === "store")
        return n.objectStoreNames[1] ? void 0 : n.objectStore(n.objectStoreNames[0]);
    }
    return I(t[e]);
  },
  set(t, e, n) {
    return t[e] = n, !0;
  },
  has(t, e) {
    return t instanceof IDBTransaction && (e === "done" || e === "store") ? !0 : e in t;
  }
};
function Vn(t) {
  ie = t(ie);
}
function Hn(t) {
  return t === IDBDatabase.prototype.transaction && !("objectStoreNames" in IDBTransaction.prototype) ? function(e, ...n) {
    const r = t.call(K(this), e, ...n);
    return He.set(r, e.sort ? e.sort() : [e]), I(r);
  } : kn().includes(t) ? function(...e) {
    return t.apply(K(this), e), I(Ve.get(this));
  } : function(...e) {
    return I(t.apply(K(this), e));
  };
}
function Un(t) {
  return typeof t == "function" ? Hn(t) : (t instanceof IDBTransaction && jn(t), Ln(t, $n()) ? new Proxy(t, ie) : t);
}
function I(t) {
  if (t instanceof IDBRequest)
    return xn(t);
  if (W.has(t))
    return W.get(t);
  const e = Un(t);
  return e !== t && (W.set(t, e), le.set(e, t)), e;
}
const K = (t) => le.get(t);
function zn(t, e, { blocked: n, upgrade: r, blocking: s, terminated: i } = {}) {
  const a = indexedDB.open(t, e), c = I(a);
  return r && a.addEventListener("upgradeneeded", (o) => {
    r(I(a.result), o.oldVersion, o.newVersion, I(a.transaction), o);
  }), n && a.addEventListener("blocked", (o) => n(
    // Casting due to https://github.com/microsoft/TypeScript-DOM-lib-generator/pull/1405
    o.oldVersion,
    o.newVersion,
    o
  )), c.then((o) => {
    i && o.addEventListener("close", () => i()), s && o.addEventListener("versionchange", (l) => s(l.oldVersion, l.newVersion, l));
  }).catch(() => {
  }), c;
}
const Wn = ["get", "getKey", "getAll", "getAllKeys", "count"], Kn = ["put", "add", "delete", "clear"], q = /* @__PURE__ */ new Map();
function Te(t, e) {
  if (!(t instanceof IDBDatabase && !(e in t) && typeof e == "string"))
    return;
  if (q.get(e))
    return q.get(e);
  const n = e.replace(/FromIndex$/, ""), r = e !== n, s = Kn.includes(n);
  if (
    // Bail if the target doesn't exist on the target. Eg, getAll isn't in Edge.
    !(n in (r ? IDBIndex : IDBObjectStore).prototype) || !(s || Wn.includes(n))
  )
    return;
  const i = async function(a, ...c) {
    const o = this.transaction(a, s ? "readwrite" : "readonly");
    let l = o.store;
    return r && (l = l.index(c.shift())), (await Promise.all([
      l[n](...c),
      s && o.done
    ]))[0];
  };
  return q.set(e, i), i;
}
Vn((t) => ({
  ...t,
  get: (e, n, r) => Te(e, n) || t.get(e, n, r),
  has: (e, n) => !!Te(e, n) || t.has(e, n)
}));
const Ue = "@firebase/installations", ue = "0.6.6";
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const ze = 1e4, We = `w:${ue}`, Ke = "FIS_v2", qn = "https://firebaseinstallations.googleapis.com/v1", Gn = 60 * 60 * 1e3, Jn = "installations", Yn = "Installations";
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const Xn = {
  "missing-app-config-values": 'Missing App configuration value: "{$valueName}"',
  "not-registered": "Firebase Installation is not registered.",
  "installation-not-found": "Firebase Installation not found.",
  "request-failed": '{$requestName} request failed with error "{$serverCode} {$serverStatus}: {$serverMessage}"',
  "app-offline": "Could not process request. Application offline.",
  "delete-pending-registration": "Can't delete installation while there is a pending registration request."
}, M = new k(Jn, Yn, Xn);
function qe(t) {
  return t instanceof y && t.code.includes(
    "request-failed"
    /* ErrorCode.REQUEST_FAILED */
  );
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
function Ge({ projectId: t }) {
  return `${qn}/projects/${t}/installations`;
}
function Je(t) {
  return {
    token: t.token,
    requestStatus: 2,
    expiresIn: Qn(t.expiresIn),
    creationTime: Date.now()
  };
}
async function Ye(t, e) {
  const r = (await e.json()).error;
  return M.create("request-failed", {
    requestName: t,
    serverCode: r.code,
    serverMessage: r.message,
    serverStatus: r.status
  });
}
function Xe({ apiKey: t }) {
  return new Headers({
    "Content-Type": "application/json",
    Accept: "application/json",
    "x-goog-api-key": t
  });
}
function Zn(t, { refreshToken: e }) {
  const n = Xe(t);
  return n.append("Authorization", er(e)), n;
}
async function Ze(t) {
  const e = await t();
  return e.status >= 500 && e.status < 600 ? t() : e;
}
function Qn(t) {
  return Number(t.replace("s", "000"));
}
function er(t) {
  return `${Ke} ${t}`;
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
async function tr({ appConfig: t, heartbeatServiceProvider: e }, { fid: n }) {
  const r = Ge(t), s = Xe(t), i = e.getImmediate({
    optional: !0
  });
  if (i) {
    const l = await i.getHeartbeatsHeader();
    l && s.append("x-firebase-client", l);
  }
  const a = {
    fid: n,
    authVersion: Ke,
    appId: t.appId,
    sdkVersion: We
  }, c = {
    method: "POST",
    headers: s,
    body: JSON.stringify(a)
  }, o = await Ze(() => fetch(r, c));
  if (o.ok) {
    const l = await o.json();
    return {
      fid: l.fid || n,
      registrationStatus: 2,
      refreshToken: l.refreshToken,
      authToken: Je(l.authToken)
    };
  } else
    throw await Ye("Create Installation", o);
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
function Qe(t) {
  return new Promise((e) => {
    setTimeout(e, t);
  });
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
function nr(t) {
  return btoa(String.fromCharCode(...t)).replace(/\+/g, "-").replace(/\//g, "_");
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const rr = /^[cdef][\w-]{21}$/, ae = "";
function sr() {
  try {
    const t = new Uint8Array(17);
    (self.crypto || self.msCrypto).getRandomValues(t), t[0] = 112 + t[0] % 16;
    const n = ir(t);
    return rr.test(n) ? n : ae;
  } catch {
    return ae;
  }
}
function ir(t) {
  return nr(t).substr(0, 22);
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
function x(t) {
  return `${t.appName}!${t.appId}`;
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const et = /* @__PURE__ */ new Map();
function tt(t, e) {
  const n = x(t);
  nt(n, e), ar(n, e);
}
function nt(t, e) {
  const n = et.get(t);
  if (n)
    for (const r of n)
      r(e);
}
function ar(t, e) {
  const n = or();
  n && n.postMessage({ key: t, fid: e }), cr();
}
let A = null;
function or() {
  return !A && "BroadcastChannel" in self && (A = new BroadcastChannel("[Firebase] FID Change"), A.onmessage = (t) => {
    nt(t.data.key, t.data.fid);
  }), A;
}
function cr() {
  et.size === 0 && A && (A.close(), A = null);
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const lr = "firebase-installations-database", ur = 1, O = "firebase-installations-store";
let G = null;
function he() {
  return G || (G = zn(lr, ur, {
    upgrade: (t, e) => {
      switch (e) {
        case 0:
          t.createObjectStore(O);
      }
    }
  })), G;
}
async function $(t, e) {
  const n = x(t), s = (await he()).transaction(O, "readwrite"), i = s.objectStore(O), a = await i.get(n);
  return await i.put(e, n), await s.done, (!a || a.fid !== e.fid) && tt(t, e.fid), e;
}
async function rt(t) {
  const e = x(t), r = (await he()).transaction(O, "readwrite");
  await r.objectStore(O).delete(e), await r.done;
}
async function j(t, e) {
  const n = x(t), s = (await he()).transaction(O, "readwrite"), i = s.objectStore(O), a = await i.get(n), c = e(a);
  return c === void 0 ? await i.delete(n) : await i.put(c, n), await s.done, c && (!a || a.fid !== c.fid) && tt(t, c.fid), c;
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
async function fe(t) {
  let e;
  const n = await j(t.appConfig, (r) => {
    const s = hr(r), i = fr(t, s);
    return e = i.registrationPromise, i.installationEntry;
  });
  return n.fid === ae ? { installationEntry: await e } : {
    installationEntry: n,
    registrationPromise: e
  };
}
function hr(t) {
  const e = t || {
    fid: sr(),
    registrationStatus: 0
    /* RequestStatus.NOT_STARTED */
  };
  return st(e);
}
function fr(t, e) {
  if (e.registrationStatus === 0) {
    if (!navigator.onLine) {
      const s = Promise.reject(M.create(
        "app-offline"
        /* ErrorCode.APP_OFFLINE */
      ));
      return {
        installationEntry: e,
        registrationPromise: s
      };
    }
    const n = {
      fid: e.fid,
      registrationStatus: 1,
      registrationTime: Date.now()
    }, r = dr(t, n);
    return { installationEntry: n, registrationPromise: r };
  } else
    return e.registrationStatus === 1 ? {
      installationEntry: e,
      registrationPromise: gr(t)
    } : { installationEntry: e };
}
async function dr(t, e) {
  try {
    const n = await tr(t, e);
    return $(t.appConfig, n);
  } catch (n) {
    throw qe(n) && n.customData.serverCode === 409 ? await rt(t.appConfig) : await $(t.appConfig, {
      fid: e.fid,
      registrationStatus: 0
      /* RequestStatus.NOT_STARTED */
    }), n;
  }
}
async function gr(t) {
  let e = await ve(t.appConfig);
  for (; e.registrationStatus === 1; )
    await Qe(100), e = await ve(t.appConfig);
  if (e.registrationStatus === 0) {
    const { installationEntry: n, registrationPromise: r } = await fe(t);
    return r || n;
  }
  return e;
}
function ve(t) {
  return j(t, (e) => {
    if (!e)
      throw M.create(
        "installation-not-found"
        /* ErrorCode.INSTALLATION_NOT_FOUND */
      );
    return st(e);
  });
}
function st(t) {
  return pr(t) ? {
    fid: t.fid,
    registrationStatus: 0
    /* RequestStatus.NOT_STARTED */
  } : t;
}
function pr(t) {
  return t.registrationStatus === 1 && t.registrationTime + ze < Date.now();
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
async function mr({ appConfig: t, heartbeatServiceProvider: e }, n) {
  const r = br(t, n), s = Zn(t, n), i = e.getImmediate({
    optional: !0
  });
  if (i) {
    const l = await i.getHeartbeatsHeader();
    l && s.append("x-firebase-client", l);
  }
  const a = {
    installation: {
      sdkVersion: We,
      appId: t.appId
    }
  }, c = {
    method: "POST",
    headers: s,
    body: JSON.stringify(a)
  }, o = await Ze(() => fetch(r, c));
  if (o.ok) {
    const l = await o.json();
    return Je(l);
  } else
    throw await Ye("Generate Auth Token", o);
}
function br(t, { fid: e }) {
  return `${Ge(t)}/${e}/authTokens:generate`;
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
async function de(t, e = !1) {
  let n;
  const r = await j(t.appConfig, (i) => {
    if (!it(i))
      throw M.create(
        "not-registered"
        /* ErrorCode.NOT_REGISTERED */
      );
    const a = i.authToken;
    if (!e && Er(a))
      return i;
    if (a.requestStatus === 1)
      return n = wr(t, e), i;
    {
      if (!navigator.onLine)
        throw M.create(
          "app-offline"
          /* ErrorCode.APP_OFFLINE */
        );
      const c = yr(i);
      return n = _r(t, c), c;
    }
  });
  return n ? await n : r.authToken;
}
async function wr(t, e) {
  let n = await De(t.appConfig);
  for (; n.authToken.requestStatus === 1; )
    await Qe(100), n = await De(t.appConfig);
  const r = n.authToken;
  return r.requestStatus === 0 ? de(t, e) : r;
}
function De(t) {
  return j(t, (e) => {
    if (!it(e))
      throw M.create(
        "not-registered"
        /* ErrorCode.NOT_REGISTERED */
      );
    const n = e.authToken;
    return Sr(n) ? Object.assign(Object.assign({}, e), { authToken: {
      requestStatus: 0
      /* RequestStatus.NOT_STARTED */
    } }) : e;
  });
}
async function _r(t, e) {
  try {
    const n = await mr(t, e), r = Object.assign(Object.assign({}, e), { authToken: n });
    return await $(t.appConfig, r), n;
  } catch (n) {
    if (qe(n) && (n.customData.serverCode === 401 || n.customData.serverCode === 404))
      await rt(t.appConfig);
    else {
      const r = Object.assign(Object.assign({}, e), { authToken: {
        requestStatus: 0
        /* RequestStatus.NOT_STARTED */
      } });
      await $(t.appConfig, r);
    }
    throw n;
  }
}
function it(t) {
  return t !== void 0 && t.registrationStatus === 2;
}
function Er(t) {
  return t.requestStatus === 2 && !Ir(t);
}
function Ir(t) {
  const e = Date.now();
  return e < t.creationTime || t.creationTime + t.expiresIn < e + Gn;
}
function yr(t) {
  const e = {
    requestStatus: 1,
    requestTime: Date.now()
  };
  return Object.assign(Object.assign({}, t), { authToken: e });
}
function Sr(t) {
  return t.requestStatus === 1 && t.requestTime + ze < Date.now();
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
async function Cr(t) {
  const e = t, { installationEntry: n, registrationPromise: r } = await fe(e);
  return r ? r.catch(console.error) : de(e).catch(console.error), n.fid;
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
async function Ar(t, e = !1) {
  const n = t;
  return await Tr(n), (await de(n, e)).token;
}
async function Tr(t) {
  const { registrationPromise: e } = await fe(t);
  e && await e;
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
function vr(t) {
  if (!t || !t.options)
    throw J("App Configuration");
  if (!t.name)
    throw J("App Name");
  const e = [
    "projectId",
    "apiKey",
    "appId"
  ];
  for (const n of e)
    if (!t.options[n])
      throw J(n);
  return {
    appName: t.name,
    projectId: t.options.projectId,
    apiKey: t.options.apiKey,
    appId: t.options.appId
  };
}
function J(t) {
  return M.create("missing-app-config-values", {
    valueName: t
  });
}
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const at = "installations", Dr = "installations-internal", Mr = (t) => {
  const e = t.getProvider("app").getImmediate(), n = vr(e), r = ce(e, "heartbeat");
  return {
    app: e,
    appConfig: n,
    heartbeatServiceProvider: r,
    _delete: () => Promise.resolve()
  };
}, Or = (t) => {
  const e = t.getProvider("app").getImmediate(), n = ce(e, at).getImmediate();
  return {
    getId: () => Cr(n),
    getToken: (s) => Ar(n, s)
  };
};
function Br() {
  R(new v(
    at,
    Mr,
    "PUBLIC"
    /* ComponentType.PUBLIC */
  )), R(new v(
    Dr,
    Or,
    "PRIVATE"
    /* ComponentType.PRIVATE */
  ));
}
Br();
T(Ue, ue);
T(Ue, ue, "esm2017");
const Y = "@firebase/remote-config", Me = "0.4.6";
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class Rr {
  constructor() {
    this.listeners = [];
  }
  addEventListener(e) {
    this.listeners.push(e);
  }
  abort() {
    this.listeners.forEach((e) => e());
  }
}
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const ot = "remote-config";
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const Fr = {
  "registration-window": "Undefined window object. This SDK only supports usage in a browser environment.",
  "registration-project-id": "Undefined project identifier. Check Firebase app initialization.",
  "registration-api-key": "Undefined API key. Check Firebase app initialization.",
  "registration-app-id": "Undefined app identifier. Check Firebase app initialization.",
  "storage-open": "Error thrown when opening storage. Original error: {$originalErrorMessage}.",
  "storage-get": "Error thrown when reading from storage. Original error: {$originalErrorMessage}.",
  "storage-set": "Error thrown when writing to storage. Original error: {$originalErrorMessage}.",
  "storage-delete": "Error thrown when deleting from storage. Original error: {$originalErrorMessage}.",
  "fetch-client-network": "Fetch client failed to connect to a network. Check Internet connection. Original error: {$originalErrorMessage}.",
  "fetch-timeout": 'The config fetch request timed out.  Configure timeout using "fetchTimeoutMillis" SDK setting.',
  "fetch-throttle": 'The config fetch request timed out while in an exponential backoff state. Configure timeout using "fetchTimeoutMillis" SDK setting. Unix timestamp in milliseconds when fetch request throttling ends: {$throttleEndTimeMillis}.',
  "fetch-client-parse": "Fetch client could not parse response. Original error: {$originalErrorMessage}.",
  "fetch-status": "Fetch server returned an HTTP error status. HTTP status: {$httpStatus}.",
  "indexed-db-unavailable": "Indexed DB is not supported by current browser"
}, f = new k("remoteconfig", "Remote Config", Fr);
function Pr(t, e) {
  return t instanceof y && t.code.indexOf(e) !== -1;
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const Nr = !1, Lr = "", Oe = 0, $r = ["1", "true", "t", "yes", "y", "on"];
class X {
  constructor(e, n = Lr) {
    this._source = e, this._value = n;
  }
  asString() {
    return this._value;
  }
  asBoolean() {
    return this._source === "static" ? Nr : $r.indexOf(this._value.toLowerCase()) >= 0;
  }
  asNumber() {
    if (this._source === "static")
      return Oe;
    let e = Number(this._value);
    return isNaN(e) && (e = Oe), e;
  }
  getSource() {
    return this._source;
  }
}
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
function is(t = Tn()) {
  return t = B(t), ce(t, ot).getImmediate();
}
async function kr(t) {
  const e = B(t), [n, r] = await Promise.all([
    e._storage.getLastSuccessfulFetchResponse(),
    e._storage.getActiveConfigEtag()
  ]);
  return !n || !n.config || !n.eTag || n.eTag === r ? !1 : (await Promise.all([
    e._storageCache.setActiveConfig(n.config),
    e._storage.setActiveConfigEtag(n.eTag)
  ]), !0);
}
function xr(t) {
  const e = B(t);
  return e._initializePromise || (e._initializePromise = e._storageCache.loadFromStorage().then(() => {
    e._isInitializationComplete = !0;
  })), e._initializePromise;
}
async function jr(t) {
  const e = B(t), n = new Rr();
  setTimeout(async () => {
    n.abort();
  }, e.settings.fetchTimeoutMillis);
  try {
    await e._client.fetch({
      cacheMaxAgeMillis: e.settings.minimumFetchIntervalMillis,
      signal: n
    }), await e._storageCache.setLastFetchStatus("success");
  } catch (r) {
    const s = Pr(
      r,
      "fetch-throttle"
      /* ErrorCode.FETCH_THROTTLE */
    ) ? "throttle" : "failure";
    throw await e._storageCache.setLastFetchStatus(s), r;
  }
}
function as(t) {
  const e = B(t);
  return Hr(e._storageCache.getActiveConfig(), e.defaultConfig).reduce((n, r) => (n[r] = Vr(t, r), n), {});
}
function Vr(t, e) {
  const n = B(t);
  n._isInitializationComplete || n._logger.debug(`A value was requested for key "${e}" before SDK initialization completed. Await on ensureInitialized if the intent was to get a previously activated value.`);
  const r = n._storageCache.getActiveConfig();
  return r && r[e] !== void 0 ? new X("remote", r[e]) : n.defaultConfig && n.defaultConfig[e] !== void 0 ? new X("default", String(n.defaultConfig[e])) : (n._logger.debug(`Returning static value for key "${e}". Define a default or remote value if this is unintentional.`), new X("static"));
}
function Hr(t = {}, e = {}) {
  return Object.keys(Object.assign(Object.assign({}, t), e));
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class Ur {
  constructor(e, n, r, s) {
    this.client = e, this.storage = n, this.storageCache = r, this.logger = s;
  }
  /**
   * Returns true if the age of the cached fetched configs is less than or equal to
   * {@link Settings#minimumFetchIntervalInSeconds}.
   *
   * <p>This is comparable to passing `headers = { 'Cache-Control': max-age <maxAge> }` to the
   * native Fetch API.
   *
   * <p>Visible for testing.
   */
  isCachedDataFresh(e, n) {
    if (!n)
      return this.logger.debug("Config fetch cache check. Cache unpopulated."), !1;
    const r = Date.now() - n, s = r <= e;
    return this.logger.debug(`Config fetch cache check. Cache age millis: ${r}. Cache max age millis (minimumFetchIntervalMillis setting): ${e}. Is cache hit: ${s}.`), s;
  }
  async fetch(e) {
    const [n, r] = await Promise.all([
      this.storage.getLastSuccessfulFetchTimestampMillis(),
      this.storage.getLastSuccessfulFetchResponse()
    ]);
    if (r && this.isCachedDataFresh(e.cacheMaxAgeMillis, n))
      return r;
    e.eTag = r && r.eTag;
    const s = await this.client.fetch(e), i = [
      // Uses write-through cache for consistency with synchronous public API.
      this.storageCache.setLastSuccessfulFetchTimestampMillis(Date.now())
    ];
    return s.status === 200 && i.push(this.storage.setLastSuccessfulFetchResponse(s)), await Promise.all(i), s;
  }
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
function zr(t = navigator) {
  return (
    // Most reliable, but only supported in Chrome/Firefox.
    t.languages && t.languages[0] || // Supported in most browsers, but returns the language of the browser
    // UI, not the language set in browser settings.
    t.language
  );
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class Wr {
  constructor(e, n, r, s, i, a) {
    this.firebaseInstallations = e, this.sdkVersion = n, this.namespace = r, this.projectId = s, this.apiKey = i, this.appId = a;
  }
  /**
   * Fetches from the Remote Config REST API.
   *
   * @throws a {@link ErrorCode.FETCH_NETWORK} error if {@link GlobalFetch#fetch} can't
   * connect to the network.
   * @throws a {@link ErrorCode.FETCH_PARSE} error if {@link Response#json} can't parse the
   * fetch response.
   * @throws a {@link ErrorCode.FETCH_STATUS} error if the service returns an HTTP error status.
   */
  async fetch(e) {
    const [n, r] = await Promise.all([
      this.firebaseInstallations.getId(),
      this.firebaseInstallations.getToken()
    ]), i = `${window.FIREBASE_REMOTE_CONFIG_URL_BASE || "https://firebaseremoteconfig.googleapis.com"}/v1/projects/${this.projectId}/namespaces/${this.namespace}:fetch?key=${this.apiKey}`, a = {
      "Content-Type": "application/json",
      "Content-Encoding": "gzip",
      // Deviates from pure decorator by not passing max-age header since we don't currently have
      // service behavior using that header.
      "If-None-Match": e.eTag || "*"
    }, c = {
      /* eslint-disable camelcase */
      sdk_version: this.sdkVersion,
      app_instance_id: n,
      app_instance_id_token: r,
      app_id: this.appId,
      language_code: zr()
      /* eslint-enable camelcase */
    }, o = {
      method: "POST",
      headers: a,
      body: JSON.stringify(c)
    }, l = fetch(i, o), h = new Promise((p, w) => {
      e.signal.addEventListener(() => {
        const ge = new Error("The operation was aborted.");
        ge.name = "AbortError", w(ge);
      });
    });
    let d;
    try {
      await Promise.race([l, h]), d = await l;
    } catch (p) {
      let w = "fetch-client-network";
      throw (p == null ? void 0 : p.name) === "AbortError" && (w = "fetch-timeout"), f.create(w, {
        originalErrorMessage: p == null ? void 0 : p.message
      });
    }
    let g = d.status;
    const m = d.headers.get("ETag") || void 0;
    let b, F;
    if (d.status === 200) {
      let p;
      try {
        p = await d.json();
      } catch (w) {
        throw f.create("fetch-client-parse", {
          originalErrorMessage: w == null ? void 0 : w.message
        });
      }
      b = p.entries, F = p.state;
    }
    if (F === "INSTANCE_STATE_UNSPECIFIED" ? g = 500 : F === "NO_CHANGE" ? g = 304 : (F === "NO_TEMPLATE" || F === "EMPTY_CONFIG") && (b = {}), g !== 304 && g !== 200)
      throw f.create("fetch-status", {
        httpStatus: g
      });
    return { status: g, eTag: m, config: b };
  }
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
function Kr(t, e) {
  return new Promise((n, r) => {
    const s = Math.max(e - Date.now(), 0), i = setTimeout(n, s);
    t.addEventListener(() => {
      clearTimeout(i), r(f.create("fetch-throttle", {
        throttleEndTimeMillis: e
      }));
    });
  });
}
function qr(t) {
  if (!(t instanceof y) || !t.customData)
    return !1;
  const e = Number(t.customData.httpStatus);
  return e === 429 || e === 500 || e === 503 || e === 504;
}
class Gr {
  constructor(e, n) {
    this.client = e, this.storage = n;
  }
  async fetch(e) {
    const n = await this.storage.getThrottleMetadata() || {
      backoffCount: 0,
      throttleEndTimeMillis: Date.now()
    };
    return this.attemptFetch(e, n);
  }
  /**
   * A recursive helper for attempting a fetch request repeatedly.
   *
   * @throws any non-retriable errors.
   */
  async attemptFetch(e, { throttleEndTimeMillis: n, backoffCount: r }) {
    await Kr(e.signal, n);
    try {
      const s = await this.client.fetch(e);
      return await this.storage.deleteThrottleMetadata(), s;
    } catch (s) {
      if (!qr(s))
        throw s;
      const i = {
        throttleEndTimeMillis: Date.now() + Tt(r),
        backoffCount: r + 1
      };
      return await this.storage.setThrottleMetadata(i), this.attemptFetch(e, i);
    }
  }
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
const Jr = 60 * 1e3, Yr = 12 * 60 * 60 * 1e3;
class Xr {
  constructor(e, n, r, s, i) {
    this.app = e, this._client = n, this._storageCache = r, this._storage = s, this._logger = i, this._isInitializationComplete = !1, this.settings = {
      fetchTimeoutMillis: Jr,
      minimumFetchIntervalMillis: Yr
    }, this.defaultConfig = {};
  }
  get fetchTimeMillis() {
    return this._storageCache.getLastSuccessfulFetchTimestampMillis() || -1;
  }
  get lastFetchStatus() {
    return this._storageCache.getLastFetchStatus() || "no-fetch-yet";
  }
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
function L(t, e) {
  const n = t.target.error || void 0;
  return f.create(e, {
    originalErrorMessage: n && (n == null ? void 0 : n.message)
  });
}
const C = "app_namespace_store", Zr = "firebase_remote_config", Qr = 1;
function es() {
  return new Promise((t, e) => {
    try {
      const n = indexedDB.open(Zr, Qr);
      n.onerror = (r) => {
        e(L(
          r,
          "storage-open"
          /* ErrorCode.STORAGE_OPEN */
        ));
      }, n.onsuccess = (r) => {
        t(r.target.result);
      }, n.onupgradeneeded = (r) => {
        const s = r.target.result;
        switch (r.oldVersion) {
          case 0:
            s.createObjectStore(C, {
              keyPath: "compositeKey"
            });
        }
      };
    } catch (n) {
      e(f.create("storage-open", {
        originalErrorMessage: n == null ? void 0 : n.message
      }));
    }
  });
}
class ts {
  /**
   * @param appId enables storage segmentation by app (ID + name).
   * @param appName enables storage segmentation by app (ID + name).
   * @param namespace enables storage segmentation by namespace.
   */
  constructor(e, n, r, s = es()) {
    this.appId = e, this.appName = n, this.namespace = r, this.openDbPromise = s;
  }
  getLastFetchStatus() {
    return this.get("last_fetch_status");
  }
  setLastFetchStatus(e) {
    return this.set("last_fetch_status", e);
  }
  // This is comparable to a cache entry timestamp. If we need to expire other data, we could
  // consider adding timestamp to all storage records and an optional max age arg to getters.
  getLastSuccessfulFetchTimestampMillis() {
    return this.get("last_successful_fetch_timestamp_millis");
  }
  setLastSuccessfulFetchTimestampMillis(e) {
    return this.set("last_successful_fetch_timestamp_millis", e);
  }
  getLastSuccessfulFetchResponse() {
    return this.get("last_successful_fetch_response");
  }
  setLastSuccessfulFetchResponse(e) {
    return this.set("last_successful_fetch_response", e);
  }
  getActiveConfig() {
    return this.get("active_config");
  }
  setActiveConfig(e) {
    return this.set("active_config", e);
  }
  getActiveConfigEtag() {
    return this.get("active_config_etag");
  }
  setActiveConfigEtag(e) {
    return this.set("active_config_etag", e);
  }
  getThrottleMetadata() {
    return this.get("throttle_metadata");
  }
  setThrottleMetadata(e) {
    return this.set("throttle_metadata", e);
  }
  deleteThrottleMetadata() {
    return this.delete("throttle_metadata");
  }
  async get(e) {
    const n = await this.openDbPromise;
    return new Promise((r, s) => {
      const a = n.transaction([C], "readonly").objectStore(C), c = this.createCompositeKey(e);
      try {
        const o = a.get(c);
        o.onerror = (l) => {
          s(L(
            l,
            "storage-get"
            /* ErrorCode.STORAGE_GET */
          ));
        }, o.onsuccess = (l) => {
          const h = l.target.result;
          r(h ? h.value : void 0);
        };
      } catch (o) {
        s(f.create("storage-get", {
          originalErrorMessage: o == null ? void 0 : o.message
        }));
      }
    });
  }
  async set(e, n) {
    const r = await this.openDbPromise;
    return new Promise((s, i) => {
      const c = r.transaction([C], "readwrite").objectStore(C), o = this.createCompositeKey(e);
      try {
        const l = c.put({
          compositeKey: o,
          value: n
        });
        l.onerror = (h) => {
          i(L(
            h,
            "storage-set"
            /* ErrorCode.STORAGE_SET */
          ));
        }, l.onsuccess = () => {
          s();
        };
      } catch (l) {
        i(f.create("storage-set", {
          originalErrorMessage: l == null ? void 0 : l.message
        }));
      }
    });
  }
  async delete(e) {
    const n = await this.openDbPromise;
    return new Promise((r, s) => {
      const a = n.transaction([C], "readwrite").objectStore(C), c = this.createCompositeKey(e);
      try {
        const o = a.delete(c);
        o.onerror = (l) => {
          s(L(
            l,
            "storage-delete"
            /* ErrorCode.STORAGE_DELETE */
          ));
        }, o.onsuccess = () => {
          r();
        };
      } catch (o) {
        s(f.create("storage-delete", {
          originalErrorMessage: o == null ? void 0 : o.message
        }));
      }
    });
  }
  // Facilitates composite key functionality (which is unsupported in IE).
  createCompositeKey(e) {
    return [this.appId, this.appName, this.namespace, e].join();
  }
}
/**
 * @license
 * Copyright 2019 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
class ns {
  constructor(e) {
    this.storage = e;
  }
  /**
   * Memory-only getters
   */
  getLastFetchStatus() {
    return this.lastFetchStatus;
  }
  getLastSuccessfulFetchTimestampMillis() {
    return this.lastSuccessfulFetchTimestampMillis;
  }
  getActiveConfig() {
    return this.activeConfig;
  }
  /**
   * Read-ahead getter
   */
  async loadFromStorage() {
    const e = this.storage.getLastFetchStatus(), n = this.storage.getLastSuccessfulFetchTimestampMillis(), r = this.storage.getActiveConfig(), s = await e;
    s && (this.lastFetchStatus = s);
    const i = await n;
    i && (this.lastSuccessfulFetchTimestampMillis = i);
    const a = await r;
    a && (this.activeConfig = a);
  }
  /**
   * Write-through setters
   */
  setLastFetchStatus(e) {
    return this.lastFetchStatus = e, this.storage.setLastFetchStatus(e);
  }
  setLastSuccessfulFetchTimestampMillis(e) {
    return this.lastSuccessfulFetchTimestampMillis = e, this.storage.setLastSuccessfulFetchTimestampMillis(e);
  }
  setActiveConfig(e) {
    return this.activeConfig = e, this.storage.setActiveConfig(e);
  }
}
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
function rs() {
  R(new v(
    ot,
    t,
    "PUBLIC"
    /* ComponentType.PUBLIC */
  ).setMultipleInstances(!0)), T(Y, Me), T(Y, Me, "esm2017");
  function t(e, { instanceIdentifier: n }) {
    const r = e.getProvider("app").getImmediate(), s = e.getProvider("installations-internal").getImmediate();
    if (typeof window > "u")
      throw f.create(
        "registration-window"
        /* ErrorCode.REGISTRATION_WINDOW */
      );
    if (!Ne())
      throw f.create(
        "indexed-db-unavailable"
        /* ErrorCode.INDEXED_DB_UNAVAILABLE */
      );
    const { projectId: i, apiKey: a, appId: c } = r.options;
    if (!i)
      throw f.create(
        "registration-project-id"
        /* ErrorCode.REGISTRATION_PROJECT_ID */
      );
    if (!a)
      throw f.create(
        "registration-api-key"
        /* ErrorCode.REGISTRATION_API_KEY */
      );
    if (!c)
      throw f.create(
        "registration-app-id"
        /* ErrorCode.REGISTRATION_APP_ID */
      );
    n = n || "firebase";
    const o = new ts(c, r.name, n), l = new ns(o), h = new Le(Y);
    h.logLevel = u.ERROR;
    const d = new Wr(
      s,
      // Uses the JS SDK version, by which the RC package version can be deduced, if necessary.
      Cn,
      n,
      i,
      a,
      c
    ), g = new Gr(d, o), m = new Ur(g, o, l, h), b = new Xr(r, m, l, o, h);
    return xr(b), b;
  }
}
/**
 * @license
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
async function os(t) {
  return t = B(t), await jr(t), kr(t);
}
rs();
export {
  ss as a,
  is as b,
  as as c,
  os as f,
  Vr as g,
  An as i,
  T as r
};
